import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, KeyRound } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) {
      setError("Le nuove password non coincidono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        <Card className="institutional-card fade-up" data-testid="reset-password-page">
          <CardContent className="p-6 sm:p-8">
            {!token ? (
              <div className="text-center" data-testid="reset-password-no-token">
                <h1 className="font-display text-xl font-bold text-slate-900">Link non valido</h1>
                <p className="mt-2 text-sm text-slate-600">Il link di reset è mancante o incompleto. Richiedine uno nuovo dalla pagina di accesso.</p>
                <Button data-testid="reset-goto-login" className="mt-6 bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/auth")}>
                  Vai al login
                </Button>
              </div>
            ) : done ? (
              <div className="text-center" data-testid="reset-password-success">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h1 className="font-display text-xl font-bold text-slate-900">Password aggiornata</h1>
                <p className="mt-2 text-sm text-slate-600">La tua nuova password è attiva. Puoi accedere con le nuove credenziali.</p>
                <Button data-testid="reset-success-goto-login" className="mt-6 bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/auth")}>
                  Vai al login
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <KeyRound className="h-5 w-5 text-[#003366]" />
                  <h1 className="font-display text-xl font-bold text-slate-900">Reimposta password</h1>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                  <div>
                    <Label htmlFor="rp-next">Nuova password (min. 8 caratteri)</Label>
                    <Input data-testid="reset-password-new" id="rp-next" type="password" required minLength={8} value={form.next}
                      onChange={(e) => setForm({ ...form, next: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="rp-confirm">Conferma nuova password</Label>
                    <Input data-testid="reset-password-confirm" id="rp-confirm" type="password" required minLength={8} value={form.confirm}
                      onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
                  </div>
                  {error && <p data-testid="reset-password-error" className="text-sm text-red-600">{error}</p>}
                  <Button data-testid="reset-password-submit" type="submit" disabled={busy} className="w-full bg-[#003366] hover:bg-[#002244]">
                    {busy ? "Aggiornamento..." : "Imposta nuova password"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
